/**
 * CprAngleService - CPR rotation angle synchronization.
 *
 * Owns the current CPR sampling-plane angle so that the AngleScrollTool,
 * the AngleSlider, and CprViewport2D instances can stay in sync through
 * the standard PubSub mechanism instead of custom callback listeners.
 *
 * Events:
 * - ANGLE_CHANGED: fired whenever the angle is updated
 */

import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';

export class CprAngleService extends PubSubService {
    static readonly SERVICE_NAME = 'CprAngleService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: CprAngleService.SERVICE_NAME,
        factory: (_context: ServiceContext) => new CprAngleService(),
    };

    readonly EVENTS = {
        ANGLE_CHANGED: 'cpr:angle:changed',
    } as const;

    private _angle: number = 0;

    normalizeAngle(value: number): number {
        return ((((value + 180) % 360) + 360) % 360) - 180;
    }

    getCurrentAngle(): number {
        return this._angle;
    }

    setCurrentAngle(angle: number): void {
        const normalizedAngle = this.normalizeAngle(angle);
        if (this._angle === normalizedAngle) return;
        this._angle = normalizedAngle;
        this.broadcast(this.EVENTS.ANGLE_CHANGED, normalizedAngle);
    }

    override reset(): void {
        this._angle = 0;
        super.reset();
    }
}
